/**
 * Phase 27 — Admin tracking & abandonment dashboard.
 *
 * Route: /admin/partners/tracking
 *
 * PINNED USER RULE (Feb 2026):
 *   Phase 27 is analytics / visibility only. No emails, SMS,
 *   notifications, ServiceM8 jobs, commission, pricing, white-label,
 *   partner-status, preferred-status or public-allocation changes.
 *   No automated retargeting. No customer or partner messaging.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, BarChart3, Download, Loader2, RefreshCw,
  AlertCircle, TrendingUp, Users, Activity,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";


const EVENT_LABELS = {
  partner_link_click:        "Partner link clicks",
  booking_started:           "Booking started",
  booking_step_viewed:       "Steps viewed",
  booking_checkout_started:  "Checkout started",
  booking_completed:         "Booking completed",
  booking_abandoned:         "Booking abandoned",
  qr_code_visit:             "QR code visits",
  marketing_kit_link_visit:  "Marketing kit links",
};


function StatTile({ label, value, sub, icon: Icon, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          {label}
        </div>
        {Icon ? <Icon className="w-4 h-4 text-cyan-600" /> : null}
      </div>
      <div className="mt-3 text-3xl font-bold text-slate-900 font-mono">
        {value}
      </div>
      {sub ? (
        <div className="mt-1 text-xs text-slate-500">{sub}</div>
      ) : null}
    </div>
  );
}


function FunnelBar({ label, current, max, testId }) {
  const pct = max ? Math.max(2, Math.round((current / max) * 100)) : 0;
  return (
    <div data-testid={testId} className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-slate-700">{label}</span>
        <span className="font-mono text-sm text-slate-900">{current}</span>
      </div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-cyan-500 to-sky-600 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


export default function PWSTracking() {
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [partners, setPartners] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const [a, b, c] = await Promise.all([
        api.get(`/admin/pws-tracking/funnel?${qs.toString()}`),
        api.get(`/admin/pws-tracking/by-partner?${qs.toString()}`),
        api.get(`/admin/pws-tracking/abandoned-sessions?limit=50`),
      ]);
      setFunnel(a.data);
      setPartners(b.data.rows || []);
      setAbandoned(c.data.rows || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load tracking");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = funnel?.totals || {};
  const rates = funnel?.rates || {};
  const maxStage = Math.max(
    totals.partner_link_click || 0,
    totals.booking_started || 0,
    totals.booking_checkout_started || 0,
    totals.booking_completed || 0,
    1,
  );

  const csvHref = useMemo(() => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    const token = localStorage.getItem("scwt_token") || "";
    return {
      href: `${API_BASE}/admin/pws-tracking/export.csv?${qs.toString()}`,
      token,
    };
  }, [dateFrom, dateTo]);

  const onDownloadCsv = async () => {
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const res = await api.get(
        `/admin/pws-tracking/export.csv?${qs.toString()}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "pws_tracking_by_partner.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("CSV download failed");
    }
  };

  if (loading && !funnel) {
    return (
      <PortalShell>
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-cyan-600 animate-spin" />
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <div
        data-testid="ph27-tracking-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8"
      >
        {/* Header */}
        <div>
          <Link
            to="/admin/partners"
            className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Partners
          </Link>
          <h1
            data-testid="ph27-page-title"
            className="font-display mt-2 text-3xl sm:text-4xl font-bold text-slate-900"
          >
            Partner traffic & booking funnel
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Operational analytics only — visibility into partner-link
            traffic, booking starts, checkout funnel and abandoned
            sessions. No emails, SMS or notifications are sent from
            this dashboard.
          </p>
        </div>

        {/* Filters */}
        <div
          data-testid="ph27-filters"
          className="flex flex-wrap items-end gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4"
        >
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              From
            </label>
            <input
              data-testid="ph27-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="block mt-1 rounded-md border-slate-300 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500">
              To
            </label>
            <input
              data-testid="ph27-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="block mt-1 rounded-md border-slate-300 text-sm"
            />
          </div>
          <button
            data-testid="ph27-refresh-btn"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="ph27-csv-export-btn"
            onClick={onDownloadCsv}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm bg-cyan-600 text-white hover:bg-cyan-700"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>

        {/* Funnel summary */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            testId="ph27-tile-clicks"
            label="Partner link clicks"
            value={(totals.partner_link_click || 0).toLocaleString()}
            sub={`${Math.round((rates.click_to_start || 0) * 100)}% click → start`}
            icon={Activity}
          />
          <StatTile
            testId="ph27-tile-starts"
            label="Booking starts"
            value={(totals.booking_started || 0).toLocaleString()}
            sub={`${Math.round((rates.start_to_checkout || 0) * 100)}% start → checkout`}
            icon={Users}
          />
          <StatTile
            testId="ph27-tile-checkout"
            label="Checkout started"
            value={(totals.booking_checkout_started || 0).toLocaleString()}
            sub={`${Math.round((rates.checkout_to_complete || 0) * 100)}% checkout → complete`}
            icon={TrendingUp}
          />
          <StatTile
            testId="ph27-tile-completed"
            label="Bookings completed"
            value={(totals.booking_completed || 0).toLocaleString()}
            sub={`${Math.round((rates.overall_click_to_complete || 0) * 100)}% overall`}
            icon={BarChart3}
          />
        </div>

        {/* Visual funnel */}
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <h2 className="font-display text-lg font-bold text-slate-900">
            Conversion funnel
          </h2>
          <div className="mt-5 space-y-4">
            <FunnelBar
              testId="ph27-funnel-clicks"
              label="Partner link clicks"
              current={totals.partner_link_click || 0}
              max={maxStage}
            />
            <FunnelBar
              testId="ph27-funnel-starts"
              label="Booking started"
              current={totals.booking_started || 0}
              max={maxStage}
            />
            <FunnelBar
              testId="ph27-funnel-checkout"
              label="Checkout started"
              current={totals.booking_checkout_started || 0}
              max={maxStage}
            />
            <FunnelBar
              testId="ph27-funnel-complete"
              label="Booking completed"
              current={totals.booking_completed || 0}
              max={maxStage}
            />
          </div>
        </div>

        {/* By partner */}
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="font-display text-lg font-bold text-slate-900">
              By partner
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Top {partners.length} partners by link clicks. Showing the
              full event breakdown.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table
              data-testid="ph27-by-partner-table"
              className="min-w-full text-sm"
            >
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] font-bold uppercase text-slate-500">
                    Partner
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-bold uppercase text-slate-500">
                    Clicks
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-bold uppercase text-slate-500">
                    Starts
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-bold uppercase text-slate-500">
                    Checkout
                  </th>
                  <th className="text-right px-3 py-2 text-[11px] font-bold uppercase text-slate-500">
                    Completed
                  </th>
                </tr>
              </thead>
              <tbody>
                {partners.length === 0 ? (
                  <tr>
                    <td
                      data-testid="ph27-by-partner-empty"
                      colSpan={5}
                      className="px-4 py-8 text-center text-sm text-slate-500"
                    >
                      No partner traffic recorded yet for this window.
                    </td>
                  </tr>
                ) : partners.map((p) => (
                  <tr
                    key={p.partner_id}
                    data-testid={`ph27-by-partner-row-${p.partner_id}`}
                    className="border-t border-slate-100"
                  >
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                      {p.partner_slug || p.partner_id}
                    </td>
                    {["partner_link_click", "booking_started",
                      "booking_checkout_started", "booking_completed"].map((t) => (
                      <td key={t} className="text-right px-3 py-2.5 font-mono">
                        {(p.counts || {})[t] || 0}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Abandoned sessions */}
        <div className="rounded-2xl bg-white border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900">
                Abandoned sessions
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Booking started but not completed within{" "}
                <span className="font-mono">{funnel?.abandonment_threshold_hours || 24}h</span>.
                No notifications sent.
              </p>
            </div>
            <span
              data-testid="ph27-abandoned-count"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200"
            >
              <AlertCircle className="w-3 h-3" /> {abandoned.length}
            </span>
          </div>
          <ul data-testid="ph27-abandoned-list" className="divide-y divide-slate-100">
            {abandoned.length === 0 ? (
              <li className="px-6 py-8 text-center text-sm text-slate-500">
                No abandoned sessions in the current window.
              </li>
            ) : abandoned.map((row) => (
              <li
                key={row.session_id}
                data-testid={`ph27-abandoned-row-${row.session_id}`}
                className="px-6 py-3 flex items-center justify-between text-sm"
              >
                <div className="font-mono text-xs text-slate-600">
                  {row.session_id.slice(0, 8)}…
                </div>
                <div className="flex gap-4 items-center text-xs">
                  <span className="text-slate-500">
                    started{" "}
                    <time className="font-mono">{row.started_at?.slice(0, 16)}</time>
                  </span>
                  <span className="text-slate-700">
                    {row.partner_slug || "direct Aware"}
                  </span>
                  <span className="text-slate-700">
                    step {row.latest_step ?? "—"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </PortalShell>
  );
}
